import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * LeadPicker — lightweight searchable picker (max 3 suggestions).
 * Calls backend /leads/lookup?q=... so visibility is enforced server-side.
 *
 * Props:
 *   value: { id, company_name, contact_name } | null
 *   onChange(lead | null): called when user clicks a suggestion or clears
 */
export default function LeadPicker({ value, onChange }) {
  const [query, setQuery] = useState(value?.company_name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);
  const containerRef = useRef(null);

  // Re-sync query if `value` is set/cleared from outside
  useEffect(() => {
    if (value) setQuery(value.company_name || "");
    else if (!value) setQuery((q) => q);
  }, [value]);

  // Click-outside closes
  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced lookup
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!query || (value && query === value.company_name)) {
      setResults([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/leads/lookup", {
          params: { q: query, limit: 3 },
        });
        setResults(data || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query, value]);

  const onPick = (lead) => {
    onChange?.(lead);
    setQuery(lead.company_name || "");
    setResults([]);
    setOpen(false);
  };

  const onType = (e) => {
    const v = e.target.value;
    setQuery(v);
    // Editing the field after a selection clears the stored lead_id
    if (value) onChange?.(null);
  };

  const clear = () => {
    onChange?.(null);
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const hasSelection = !!value?.id && query === value.company_name;

  return (
    <div ref={containerRef} className="relative" data-testid="lead-picker">
      <div className="relative">
        <MagnifyingGlass
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        <input
          type="text"
          data-testid="lead-picker-input"
          value={query}
          onChange={onType}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Type company or contact name..."
          autoComplete="off"
          className={`w-full pl-9 pr-9 py-2 border rounded-md text-sm ${
            hasSelection
              ? "border-emerald-300 bg-emerald-50/40 text-gray-900"
              : "border-gray-300"
          }`}
        />
        {query && (
          <button
            type="button"
            data-testid="lead-picker-clear"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      {open && (results.length > 0 || loading) && (
        <div
          data-testid="lead-picker-results"
          className="absolute z-30 mt-1 left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-gray-400">Searching…</div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onPick(r)}
                data-testid={`lead-picker-option-${r.id}`}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-b-0 border-gray-100"
              >
                <span className="font-medium text-gray-900">{r.company_name}</span>
                {r.contact_name && (
                  <>
                    <span className="text-gray-400 mx-1">—</span>
                    <span className="text-gray-600">{r.contact_name}</span>
                  </>
                )}
              </button>
            ))}
          {!loading && results.length === 0 && query && (
            <div className="px-3 py-2 text-xs text-gray-400">No matches.</div>
          )}
        </div>
      )}
    </div>
  );
}
